import React, { useState, useEffect } from 'react';
import { Search, Plus, Filter } from 'lucide-react';
import SocialAccountsManager from '../components/SocialAccountsManager';
import ContentPipeline from '../components/ContentPipeline';
import TrendingFeed from '../components/TrendingFeed';
import ProjectPromotions from '../components/ProjectPromotions';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { projectService } from '../../projects';
import { useProjectStore } from '../../../stores/projectsSlice';
import { toast } from 'react-hot-toast';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const statusStyle = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'active':    return { backgroundColor: SYSTEM_COLORS.successTint,  color: SYSTEM_COLORS.success };
    case 'upcoming':  return { backgroundColor: SYSTEM_COLORS.warningTint,  color: SYSTEM_COLORS.warning };
    case 'completed': return { backgroundColor: SYSTEM_COLORS.infoTint,     color: SYSTEM_COLORS.info    };
    default:          return { backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
  }
};

const TABS = [
  { id: 'overview',       label: 'Overview'      },
  { id: 'campaigns',      label: 'Promotions'    },
  { id: 'content',        label: 'Content'       },
  { id: 'trending',       label: 'Trending'      },
  { id: 'social-accounts',label: 'Social'        },
  { id: 'calendar',       label: 'Calendar'      },
  { id: 'analytics',      label: 'Analytics'     },
];

const MarketingPage = () => {
  const { user } = useApiAuth();
  const projectsObj = useProjectStore(state => state.projects);
  const setProjects  = useProjectStore(state => state.setProjects);
  const projects     = Array.isArray(projectsObj) ? projectsObj : Object.values(projectsObj || {});

  const [activeTab,        setActiveTab]        = useState('overview');
  const [searchQuery,      setSearchQuery]      = useState('');
  const [loading,          setLoading]          = useState(true);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const response = await projectService.getProjects();
        if (mounted) setProjects(response.projects || []);
      } catch {
        toast.error('Failed to load projects');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user, setProjects]);

  const filtered = projects.filter(p =>
    !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-5">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em]"
             style={{ color: BRAND_COLORS.primary }}>
            Growth
          </p>
          <h1 className="mt-0.5 text-2xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>
            Marketing
          </h1>
        </div>

      </div>

      {/* ── Tab nav ────────────────────────────────────────────────── */}
      <div
        className="flex overflow-x-auto border-b scrollbar-none"
        style={{ borderColor: SYSTEM_COLORS.borderSoft }}
      >
        {TABS.map(({ id, label }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className="flex-shrink-0 px-3 sm:px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors"
              style={{
                borderBottomColor: active ? BRAND_COLORS.primary : 'transparent',
                color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                backgroundColor: 'transparent',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* ── Tab content ────────────────────────────────────────────── */}

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* Search + filter */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div
              className="flex flex-1 items-center gap-2 rounded-xl border px-3 py-2"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
            >
              <Search size={14} style={{ color: SYSTEM_COLORS.textSoft, flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search projects…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="flex-1 text-sm bg-transparent outline-none min-w-0"
                style={{ color: NEUTRAL_COLORS.black }}
              />
            </div>
            <button
              className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium flex-shrink-0"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted, backgroundColor: SYSTEM_COLORS.shellSurface }}
            >
              <Filter size={14} /> Filter
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center h-40 rounded-xl border"
                 style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
              <div className="h-8 w-8 animate-spin rounded-full border-b-2"
                   style={{ borderColor: BRAND_COLORS.primary }} />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border"
                 style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
              <table className="min-w-full">
                <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                  <tr>
                    {['Project', 'Status', 'Budget', 'Team', 'Next Milestone'].map(h => (
                      <th key={h}
                          className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                          style={{ color: SYSTEM_COLORS.textMuted }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-sm"
                          style={{ color: SYSTEM_COLORS.textMuted }}>
                        No projects found
                      </td>
                    </tr>
                  ) : filtered.map(project => (
                    <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium"
                          style={{ color: NEUTRAL_COLORS.black }}>
                        {project.name}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize"
                              style={statusStyle(project.status)}>
                          {project.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: NEUTRAL_COLORS.black }}>
                        ${(project.budget || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                        {project.teamSize ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                        {project.nextMilestone ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && (
            <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              Showing {filtered.length} of {projects.length} projects
            </p>
          )}
        </div>
      )}

      {/* Promotions */}
      {activeTab === 'campaigns' && <ProjectPromotions />}

      {/* Content Pipeline */}
      {activeTab === 'content' && <ContentPipeline />}

      {/* Trending & Inspiration */}
      {activeTab === 'trending' && <TrendingFeed />}

      {/* Social Accounts */}
      {activeTab === 'social-accounts' && <SocialAccountsManager />}

      {/* Calendar */}
      {activeTab === 'calendar' && (
        <div className="rounded-xl border p-5"
             style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
          <p className="text-sm font-semibold mb-4" style={{ color: NEUTRAL_COLORS.black }}>
            Content Calendar
          </p>
          <div className="grid grid-cols-7 gap-2 text-center text-xs font-medium mb-2"
               style={{ color: SYSTEM_COLORS.textMuted }}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d}>{d}</div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics */}
      {activeTab === 'analytics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[
            {
              title: 'Campaign Performance',
              items: [{ label: 'Social Media Blitz', pct: 75 }, { label: 'Email Campaign', pct: 60 }],
            },
            {
              title: 'Budget Overview',
              items: [
                { label: 'Total Budget', pct: 70, value: '$250,000' },
                { label: 'Spent',        pct: 70, value: '$175,000' },
              ],
            },
          ].map(({ title, items }) => (
            <div
              key={title}
              className="rounded-xl border p-5"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
            >
              <p className="text-sm font-semibold mb-4" style={{ color: NEUTRAL_COLORS.black }}>{title}</p>
              <div className="space-y-4">
                {items.map(({ label, pct, value }) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span style={{ color: SYSTEM_COLORS.textMuted }}>{label}</span>
                      <span className="font-medium" style={{ color: NEUTRAL_COLORS.black }}>
                        {value || `${pct}%`}
                      </span>
                    </div>
                    <div className="h-2 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${pct}%`, backgroundColor: BRAND_COLORS.primary }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default MarketingPage;
